import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { SpeakerWaveIcon } from '@heroicons/react/24/outline';

/**
 * The call recordings, playable inside the Qualifying Call replay modal: one player per call
 * (22 Oct — before, each call's recording replaced the one before and only the last call could
 * be played).
 *
 * The server hands out a presigned S3 link valid for 1 hour, minted when the modal opens (never
 * the S3 key), one per recording (`?recording=<id>`). S3 answers the browser's Range requests
 * itself, so seeking works without the API in the middle. A link that has expired while the
 * modal sat open fails with a media error; the player fetches a fresh one once and carries on
 * from the same position.
 *
 * `registerAudio(id, element)` lets the modal seek a call's player from a transcript line;
 * `onReadyChange(id, ready)` tells it which players can be seeked.
 *
 * Props: screeningId, candidateId, recordings [{ id, status, attempt, durationMs, approximate }],
 *   hadCall (a call reached the transcript, so a recording is on its way), registerAudio,
 *   onReadyChange.
 */
export default function QualifyingCallRecordingPlayer({
  screeningId,
  candidateId,
  recordings = [],
  hadCall,
  registerAudio,
  onReadyChange,
}) {
  if (!recordings.length && !hadCall) {
    return null;
  }
  const approximate = recordings.some((r) => r.approximate);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4" data-testid="qualifying-recordings">
      <div className="mb-2.5 flex items-center gap-2">
        <SpeakerWaveIcon className="h-4 w-4 text-[#007BFF]" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
          {recordings.length > 1 ? 'Call recordings' : 'Call recording'}
        </p>
        {approximate && <span className="text-[11px] text-gray-400">· transcript times are approximate</span>}
      </div>
      {recordings.length === 0 ? (
        <p className="text-xs text-gray-500">The call recording is being processed — it appears here a minute after the call ends.</p>
      ) : (
        <div className="space-y-2.5">
          {recordings.map((recording, i) => (
            <RecordingRow
              key={recording.id || i}
              screeningId={screeningId}
              candidateId={candidateId}
              recording={recording}
              label={
                recordings.length > 1
                  ? recording.attempt
                    ? `Call ${recording.attempt}`
                    : `Recording ${i + 1}`
                  : ''
              }
              registerAudio={registerAudio}
              onReadyChange={onReadyChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RecordingRow({ screeningId, candidateId, recording, label, registerAudio, onReadyChange }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const retried = useRef(false);
  const audio = useRef(null);
  const { id, status = 'none' } = recording;

  const load = useCallback(() => {
    setState('loading');
    return axios
      .get(`/api/screenings/${screeningId}/qualifying-agent-session/${candidateId}/recording`, {
        // No id: a server from before 22 Oct, which has only the latest.
        ...(id ? { params: { recording: id } } : {}),
      })
      .then((res) => {
        setUrl(res.data?.url || null);
        setState(res.data?.url ? 'ready' : 'error');
      })
      .catch(() => setState('error'));
  }, [screeningId, candidateId, id]);

  useEffect(() => {
    retried.current = false;
    if (status === 'stored' && screeningId && candidateId) {
      load();
    }
  }, [status, screeningId, candidateId, load]);

  useEffect(() => {
    onReadyChange?.(id, state === 'ready');
  }, [id, state, onReadyChange]);

  const setAudio = useCallback(
    (element) => {
      audio.current = element;
      registerAudio?.(id, element);
    },
    [id, registerAudio]
  );

  const onMediaError = () => {
    if (retried.current) {
      setState('error');
      return;
    }
    retried.current = true;
    const at = audio.current?.currentTime || 0;
    load().then(() => {
      if (audio.current) audio.current.currentTime = at;
    });
  };

  let body;
  if (status === 'stored' && state === 'ready' && url) {
    body = <audio ref={setAudio} src={url} controls preload="metadata" onError={onMediaError} className="w-full" />;
  } else if (status === 'stored' && state !== 'error') {
    body = <p className="text-xs text-gray-500">Loading recording…</p>;
  } else {
    body = <p className="text-xs text-gray-500">This recording could not be loaded. Try reopening this window.</p>;
  }

  if (!label) {
    return body;
  }
  return (
    <div data-testid="qualifying-recording">
      <p className="mb-1 text-xs font-semibold text-gray-700">{label}</p>
      {body}
    </div>
  );
}
